import { toast } from 'react-toastify';
import { AxiosError } from 'axios';

import useSyncMutation from '../useSyncMutation';

import { useLanguageContext } from '~providers';
import { postRequest } from '~services';
import { MutateContext } from '~types';

type SetupIntentCreateRequest = {
  // Whether Stripe may redirect for 3DS/wallet auth fallback. Defaults to
  // `false` on the backend; the wallet flow keeps it off to stay inline.
  allowRedirects?: boolean;
};

type SetupIntentCreateResponse = {
  setupIntentId: string;
  clientSecret: string;
};

/**
 * Creates a Stripe SetupIntent on the backend (`POST api/payment/create-setup-intent`,
 * ENG-7871). The returned `clientSecret` is used by the client to confirm a card
 * or wallet payment method via `stripe.confirmSetup` — replacing the legacy
 * `stripe.createPaymentMethod` tokenization.
 *
 * Shared building block for the wallet flow (ENG-7890) and the card add flow
 * (ENG-7877).
 */
export const useMutationCreateSetupIntent = ({
  shouldToast = false,
  onErrorCallback,
}: MutateContext & { onErrorCallback?: (errorMsg: string) => void } = {}) => {
  const { lt } = useLanguageContext();

  return useSyncMutation(
    async (
      request: SetupIntentCreateRequest,
    ): Promise<SetupIntentCreateResponse> => {
      // `postRequest`'s generic types the request body, not the response, so it
      // resolves to a raw AxiosResponse — read + cast `.data` (same pattern as
      // the other payment mutations).
      const response = await postRequest<SetupIntentCreateRequest>({
        path: 'api/payment/create-setup-intent',
        data: request,
      });

      return response.data as SetupIntentCreateResponse;
    },
    {
      onError(error) {
        const message =
          (error instanceof AxiosError
            ? error.response?.data?.message
            : undefined) ??
          lt(
            'text.hooks.useMutationCreateSetupIntent.error',
            'Something went wrong when preparing payment',
          );

        if (shouldToast) {
          toast.error(message);
        }

        onErrorCallback?.(message);
      },
    },
  );
};
